import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Eye, EyeOff } from 'lucide-react'

export function ChangePasswordScreen({ onComplete }: { onComplete: () => void }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')
  const [show, setShow]         = useState(false)
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setLoading(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    if (updateError) { setError(updateError.message); setLoading(false); return }
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      await supabase.from('profiles')
        .update({ requires_password_change: false })
        .eq('user_id', user.id)
    }
    onComplete()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D2240] dark:bg-[#1a2535]">
      <div className="bg-white dark:bg-[#0D2240] rounded-2xl p-8 w-[400px] shadow-2xl">
        <div className="text-center mb-6">
          <div className="text-[#C8A84B] font-bold text-xl tracking-widest uppercase mb-3">
            CONORBIT
          </div>
          <h2 className="text-[18px] font-semibold text-[#0D2240] dark:text-white">Set Your Password</h2>
          <p className="text-[12.5px] text-muted-foreground mt-2 text-justify px-2">
            Your account was created by an administrator. Please set a personal
            password to secure your account before continuing.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              className="w-full px-4 py-3 pr-10 border border-[#DDD8CE] dark:border-white/20 rounded-xl text-[13px] bg-background dark:text-white focus:outline-none focus:border-[#006B3C]"
            />
            <button
              type="button"
              onClick={() => setShow(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <input
            type={show ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-4 py-3 border border-[#DDD8CE] dark:border-white/20 rounded-xl text-[13px] bg-background dark:text-white focus:outline-none focus:border-[#006B3C]"
          />
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2.5 text-[12px] text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#006B3C] text-white rounded-xl text-[13px] font-semibold hover:bg-[#005A32] disabled:opacity-50 transition-colors"
          >
            {loading ? 'Setting password…' : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}
